import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '@/lib/utils/cn';

const alertVariants = cva(
  // The icon spans 2 rows ONLY when an AlertTitle is present. With a
  // description alone the icon's span would add an empty second row, and the
  // grid's `gap-2` renders as 8px of dead space under the text (16px padding
  // above, 24px below). The `has-data-[slot=alert-title]` selector is more
  // specific than the bare `*:[svg]` one, so it wins whenever a title exists
  // regardless of utility source order.
  "group/alert relative grid w-full gap-2 rounded-lg border p-4 text-left text-xs/relaxed has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-1 has-data-[slot=alert-title]:*:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-3.5",
  {
    variants: {
      // Standardized banner palette — every tone follows the same recipe:
      //   border-{tone}/30 · bg-{tone}/10 · foreground body · colored icon.
      // This keeps notices legible (dark text on a soft tint) and visually
      // consistent regardless of severity. `destructive` keeps red body text
      // because the colour itself carries the "error" signal.
      variant: {
        default: 'border-border bg-muted/40 text-foreground',
        destructive:
          'border-destructive/30 bg-destructive/10 text-destructive *:data-[slot=alert-description]:text-destructive/90 *:[svg]:text-destructive',
        info: 'border-info/30 bg-info/10 text-foreground *:[svg]:text-info',
        success:
          'border-success/30 bg-success/10 text-foreground *:[svg]:text-success',
        warning:
          'border-warning/30 bg-warning/10 text-foreground *:[svg]:text-warning',
        ai: 'border-ai/30 bg-ai/10 text-foreground *:[svg]:text-ai',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot='alert'
      role='alert'
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-title'
      className={cn(
        'font-heading font-semibold group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  );
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-description'
      className={cn(
        'text-xs/relaxed text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className,
      )}
      {...props}
    />
  );
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot='alert-action'
      className={cn('absolute top-1.5 right-2', className)}
      {...props}
    />
  );
}

export { Alert, AlertAction, AlertDescription, AlertTitle };
